import {
  api,
  formatError,
  getCachedWorkspace,
  saveWorkspaceToConfig,
  clearActiveWorkspace,
  fetchAuthorizedWorkspaces,
  reconcileActiveWorkspace,
} from '../api-client'
import { getGitRemote, isGitRepo, getChangedFilesList, getRepoRoot } from '../git'
import { existsSync, readFileSync } from 'node:fs'
import { resolve } from 'node:path'
import { analyticsCommandError, analyticsEvent, flushAnalytics, flushAndExit } from '../analytics'

// Helpers extracted into ./context/* — this file keeps resolveWorkspaceSlug
// and the command dispatcher. See ./context/{args,render,validation,help,types}.ts
import { parseArgs, collectMulti } from './context/args'
import { emitJSON, renderSummaryHuman, renderContextHuman, renderMatchFilesHuman } from './context/render'
import {
  toSlug,
  checkFieldSize,
  checkRel,
  hasCommaOutsideBraces,
  warnIfConflictingAppend,
  mergeAppendField,
  validateAndReportPatterns,
  warnIfOverBroadAnchor,
  printVaultValidation,
  warnIfDocShapedField,
  checkSecretsOrExit,
  isAllowSecrets,
} from './context/validation'
import { printAddHelp, printUpdateHelp, printRelationsHelp, printGraphHelp, printMergeHelp } from './context/help'
import { assertKnownFlags } from './context/flags'
import { commentCommand } from './context/comment'
import { resolveAreaId } from './area'
import { parseAnchorFlag, parseRuleFlag, splitAnchors, externalDirectoryPath } from './context/anchor'
import { MAX_CONTENT_SIZE } from './context/types'
import type { CanonicalSummary, CanonicalContext, MatchFilesResult, TopicRelation } from './context/types'

// Opaque offset cursor for `context list` paging of a large vault. base64url
// {o:offset}; a malformed cursor resets to 0 (degrades to first page, never errors).
export function encodeListCursor(offset: number): string {
  return Buffer.from(JSON.stringify({ o: offset })).toString('base64url')
}
export function decodeListCursor(raw: unknown): number {
  if (typeof raw !== 'string' || raw.trim() === '') return 0
  try {
    const o = Number((JSON.parse(Buffer.from(raw, 'base64url').toString('utf8')) as { o?: unknown })?.o)
    return Number.isInteger(o) && o >= 0 ? o : 0
  } catch {
    return 0
  }
}

export async function resolveWorkspaceSlug(): Promise<string> {
  // Prefer the explicitly-active workspace (set by `workspace switch` or `login`).
  // GET /me only ever resolves the credential's PRIMARY workspace, so calling it
  // first would silently override a `workspace switch` — and clobber the saved
  // slug. With scoped credentials the active workspace is the source of truth.
  const active = getCachedWorkspace().slug
  if (active) {
    // …but the cached active slug can be STALE for the CURRENT key (key rotated,
    // membership dropped, config hand-edited). Replaying it produces a confusing
    // `/workspaces/<slug>` 403 ("does not match the API key") before any real
    // command runs. Reconcile against /me/workspaces — the list the key can
    // ACTUALLY reach — and repair the cache instead of surfacing the 403.
    const authorized = await fetchAuthorizedWorkspaces()
    const verdict = reconcileActiveWorkspace(active, authorized)
    switch (verdict.action) {
      case 'ok':
      case 'unknown':
        // Authorized, or we couldn't ask (offline) — trust the cache, never
        // repair on a network blip.
        return active
      case 'repaired': {
        // Stale slug, exactly one authorized workspace → adopt it unambiguously.
        const repaired = authorized!.find((w) => w.slug === verdict.slug)
        clearActiveWorkspace()
        if (repaired) saveWorkspaceToConfig(repaired.slug, repaired.id ?? repaired.workspace_id)
        console.error(
          `Note: active workspace '${active}' is not usable with the current API key — using '${verdict.slug}' instead.`,
        )
        return verdict.slug!
      }
      case 'ambiguous': {
        // Stale slug, several authorized workspaces → we cannot guess. Clear the
        // bad slug and point the user at `workspace switch` (clear path, no 403).
        clearActiveWorkspace()
        console.error(
          `Error: active workspace '${active}' is not authorized for the current API key.\n` +
            `  Authorized: ${verdict.authorized.join(', ')}\n` +
            `  Pick one:   driftless workspace switch <slug>`,
        )
        process.exit(1)
      }
      case 'cleared': {
        // Stale slug and the key reaches NO workspaces — clear and error.
        clearActiveWorkspace()
        console.error(
          `Error: active workspace '${active}' is not authorized for the current API key, ` +
            `and the key has no other workspaces. Log in again or add a key: driftless login`,
        )
        process.exit(1)
      }
    }
  }
  // No active workspace configured yet → resolve from the credential and remember it.
  try {
    const me = (await api.get('/me', { timeoutMs: 8000, retries: 1 })) as {
      workspace_id?: string
      slug?: string
    }
    if (me?.slug) {
      saveWorkspaceToConfig(me.slug, me.workspace_id)
      return me.slug
    }
  } catch {
    // fall back to cached identity / git remote
  }
  const cached = getCachedWorkspace().slug
  if (cached) return cached
  const remote = getGitRemote()
  if (!remote) {
    // BUG-02 (staging QA): this is the END of the whole resolution chain — no
    // active workspace, /me unreachable with the current credentials, no cached
    // slug AND no usable git remote. Saying only "no git remote found" sent
    // users chasing git when the actual fix is almost always credentials.
    console.error(
      'Error: could not resolve a workspace.\n' +
        '  Tried: active workspace (none) → /me with current credentials (unreachable or no key) → git remote (none/unparseable).\n' +
        '  Fix: driftless login   ·   or set DRIFTLESS_API_KEY   ·   or run from a checkout with a GitHub remote.',
    )
    process.exit(1)
  }
  return remote.org
}

// ─── Main command ───────────────────────────────────────────────────────────

export async function contextCommand(args: string[]): Promise<void> {
  const { flags, positional } = parseArgs(args)
  const subCommand = positional[0]
  // Human by default, --json for agents
  const isJSON = !!flags['json']

  // Help is local-only — no network, no git repo required. Users should be
  // able to `driftless context <subcommand> --help` from anywhere to see options.
  if (flags['help'] === true || positional[1] === 'help') {
    if (subCommand === 'add') { printAddHelp(); process.exit(0) }
    if (subCommand === 'update') { printUpdateHelp(); process.exit(0) }
    if (subCommand === 'relations') { printRelationsHelp(); process.exit(0) }
    if (subCommand === 'graph') { printGraphHelp(); process.exit(0) }
    if (subCommand === 'merge') { printMergeHelp(); process.exit(0) }
    if (subCommand === 'retrieve') {
      console.log(`Usage: driftless context retrieve "<task>" [--files a,b] [--tag t] [--area id] [--stale] [--status s] [--limit n] [--view brief|full] [--explain] [--include-notes] [--json]\n\nThe unified intent ranker: one bounded call to find the right context for a task across a large vault (query and/or files), ranked drifted-first with trust + why_matched. --explain shows why each topic matched.`)
      process.exit(0)
    }
    if (subCommand === 'list' || subCommand === 'get' || subCommand === 'search' || subCommand === 'doctor' || subCommand === 'link' || subCommand === 'sync' || subCommand === 'share' || subCommand === 'shared' || subCommand === 'coverage' || subCommand === 'events') {
      console.log(`Usage: driftless context ${subCommand} [args] [flags]\n\nRun 'driftless help context' for full reference.`)
      process.exit(0)
    }
    if (subCommand === 'move') {
      console.log(`Usage: driftless context move <slug> --to <workspace-slug>\n\nMove a topic to another workspace you belong to. It re-homes there as a Note\n(approval doesn't carry across workspaces). Needs owner/admin in the source.`)
      process.exit(0)
    }
    if (subCommand === 'approve' || subCommand === 'propose' || subCommand === 'reject' || subCommand === 'archive') {
      console.log(`Usage: driftless context ${subCommand} <slug>\n\nThe trust model is two states: you write Notes; merging a note into Knowledge is an owner/admin act.\n  Note (your draft) → Knowledge (the team's source of truth — the notes merged in).\nLifecycle: propose (put a note up for review) · approve (merge the note into Knowledge) · reject [--reason] (back to a Note, with feedback) · archive.\napprove/reject need owner/admin authority — run the merge only when an owner/admin explicitly asks, never unprompted.`)
      process.exit(0)
    }
    if (subCommand === 'pr') {
      console.log(`Usage: driftless context pr <slug> [--open --summary "..." <content flags> | --merge <id> | --reject <id>]\n\nSuggested edits: suggest a content change to existing Knowledge, a human reviews it, then merges or rejects.`)
      process.exit(0)
    }
  }

  // `context comment` is its own sub-surface (annotations on a spine object /
  // card). Dispatch it before assertKnownFlags — it owns flags (--body/--field/
  // --status) the topic commands don't know — and it needs no git repo.
  if (subCommand === 'comment') {
    await commentCommand(args.slice(1))
    return
  }

  // Reject unknown flags BEFORE any network/handler runs (but AFTER --help, so
  // help always works). Turns a silently-dropped typo into a clear error.
  assertKnownFlags(flags)

  // Repo requirement is per-command, not blanket. Topic reads are
  // workspace-scoped — recalling a decision/runbook, searching, listing, or
  // inspecting relations must work from anywhere, not only inside a checkout.
  // Only commands that touch the working tree or the current remote need a repo:
  //   add / update — validate glob anchors against the local checkout
  //   link         — register the current repo's remote into where_repos
  //   sync         — repo-scoped drift
  //   get --diff   — reads uncommitted changes from the working tree
  // (This used to be a blanket guard — a leftover from the git-first era before
  // Driftless became topic-first and language-agnostic.)
  const repoBackedCommand =
    subCommand === 'add' ||
    subCommand === 'update' ||
    subCommand === 'link' ||
    subCommand === 'sync' ||
    (subCommand === 'get' && !!flags['diff'])
  if (repoBackedCommand && !isGitRepo()) {
    console.error(
      `Error: 'driftless context ${subCommand}' works against your local checkout — run it inside a git repository.`,
    )
    process.exit(1)
  }

  // Validate required positional args before the first network call so a
  // missing-arg error surfaces ahead of auth/workspace errors in unconfigured
  // environments. (resolveWorkspaceSlug makes an HTTP call.)
  const SLUG_REQUIRED_CMDS = new Set([
    'update', 'approve', 'propose', 'reject', 'archive', 'delete', 'merge',
    'share', 'move', 'pr', 'events', 'coverage', 'relations', 'graph',
    'link', 'sync',
  ])
  if (SLUG_REQUIRED_CMDS.has(subCommand) && !positional[1]) {
    console.error(`Error: 'driftless context ${subCommand}' requires a topic slug.`)
    console.error(`Usage: driftless context ${subCommand} <slug>`)
    process.exit(1)
  }
  if (subCommand === 'add' && !positional[1]) {
    printAddHelp(process.stderr)
    process.exit(1)
  }
  if (subCommand === 'get' && !positional[1] && !flags['files'] && !flags['diff']) {
    console.error(`Error: 'driftless context get' requires a topic slug, --files, or --diff.`)
    console.error(`Usage: driftless context get <topic>\n       driftless context get --files "path1,path2"\n       driftless context get --diff [--mark]`)
    process.exit(1)
  }
  if (subCommand === 'search' && !positional[1]) {
    console.error(`Error: 'driftless context search' requires a query term.`)
    console.error(`Usage: driftless context search <query> [--limit N] [--tag <tag>]`)
    process.exit(1)
  }

  const workspaceSlug = await resolveWorkspaceSlug()

  // ─── doctor (context audit) ──────────────────────────────────────────
  if (subCommand === 'doctor') {
    type Entry = { slug: string; reason?: string }
    type Audit = {
      total: number
      stale: Entry[]; orphaned: Entry[]; draft: Entry[]
      docs_pending: Entry[]; repo_leak: Entry[]
      mis_shaped_how?: Entry[]
      unassigned?: Entry[]
      duplicates?: Entry[]
      zombie?: Entry[]
    }
    let audit: Audit
    try {
      audit = (await api.get(`/workspaces/${workspaceSlug}/topics/audit`)) as Audit
    } catch (e: any) {
      console.error(`Failed to audit context: ${formatError(e)}`)
      process.exit(1)
    }

    // Zombie detection — local-only pass. A topic is zombie when its
    // anchor patterns matched files at write time but, in this checkout,
    // every pattern now returns 0 hits (files moved, glob outdated, repo
    // restructured). We can only check this for topics anchored to the
    // repo we are currently sitting in.
    const zombie: Entry[] = []
    const currentRemote = getGitRemote()
    const repoRoot = getRepoRoot()
    if (currentRemote && repoRoot) {
      try {
        const repos = (await api.get(`/workspaces/${workspaceSlug}/repos`)) as Array<{ id: string; github_org: string; github_repo: string }>
        const repoMatch = repos.find((r) => r.github_org === currentRemote.org && r.github_repo === currentRemote.repo)
        if (repoMatch) {
          const summaries = (await api.get(`/workspaces/${workspaceSlug}/topics?repo=${encodeURIComponent(repoMatch.id)}&suggested=false`)) as CanonicalSummary[]
          const { globSync } = require('node:fs') as typeof import('node:fs')
          for (const s of summaries) {
            const patterns = s.anchors?.patterns ?? []
            if (patterns.length === 0) continue
            let total = 0
            for (const p of patterns) {
              try {
                total += (globSync(p, { cwd: repoRoot }) as string[]).length
                if (total > 0) break
              } catch { /* skip invalid glob */ }
            }
            if (total === 0) {
              zombie.push({ slug: s.topic, reason: `anchor patterns match 0 files in ${currentRemote.org}/${currentRemote.repo}` })
            }
          }
        }
      } catch { /* zombie check is best-effort; never fail doctor on it */ }
    }
    audit.zombie = zombie

    if (isJSON) {
      emitJSON(audit)
      const blocking = audit.orphaned.length + audit.repo_leak.length
      analyticsEvent('cli_context_doctor', workspaceSlug, {
        stale_count: audit.stale.length,
        orphaned_count: audit.orphaned.length,
        draft_count: audit.draft.length,
        zombie_count: zombie.length,
      })
      await flushAndExit(blocking > 0 ? 1 : 0)
    }

    const section = (label: string, items: Entry[]) => {
      if (items.length === 0) return
      console.log(`\n${label} (${items.length}):`)
      for (const it of items) {
        console.log(`  ${it.slug}${it.reason ? `  — ${it.reason}` : ''}`)
      }
    }

    console.log(`Context audit — ${audit.total} topic${audit.total === 1 ? '' : 's'}`)
    section('⚠ stale (code changed, context not updated)', audit.stale)
    section('✗ orphaned (repo deleted — hidden from agents)', audit.orphaned)
    section('⚠ zombie (anchor patterns match 0 files in this checkout)', zombie)
    section('• draft (suggested, never confirmed)', audit.draft)
    section('• docs-pending (doc anchored, never synced)', audit.docs_pending)
    section('• mis-shaped how (long-form in how → move to content)', audit.mis_shaped_how ?? [])
    section('⚠ unassigned (no area — vaults rot here)', audit.unassigned ?? [])
    section('⚠ duplicates (same title or identical anchors — consolidate, rule E)', audit.duplicates ?? [])
    section('✗ repo-leak (references unknown repo ids)', audit.repo_leak)

    const misShapedHow = audit.mis_shaped_how ?? []
    const unassigned = audit.unassigned ?? []
    const duplicates = audit.duplicates ?? []
    const blocking = audit.orphaned.length + audit.repo_leak.length
    if (
      audit.stale.length + audit.draft.length + audit.docs_pending.length + misShapedHow.length + zombie.length + unassigned.length + duplicates.length + blocking === 0
    ) {
      console.log('\nAll context is healthy.')
    } else {
      if (blocking > 0) {
        console.log('\nResolve orphaned / repo-leak topics — they indicate deleted-repo fallout.')
      }
      if (zombie.length > 0) {
        console.log('\nZombie topics: re-anchor with `driftless context update <slug> --add-pattern "<glob>" --remove-pattern "<old>"`, or archive if obsolete.')
      }
      if (misShapedHow.length > 0) {
        console.log('\nMis-shaped how: move the long-form into content — `driftless context update <slug> --content @doc.md --how "<1–3 sentence summary>"`.')
      }
      if (unassigned.length > 0) {
        console.log('\nUnassigned topics: file each into a domain — `driftless context update <slug> --area <name>` (see `driftless area list`).')
      }
      if (duplicates.length > 0) {
        console.log('\nDuplicates: consolidate into ONE topic (rule E) — rewrite the survivor coherently, then archive or delete the rest.')
      }
    }
    analyticsEvent('cli_context_doctor', workspaceSlug, {
      stale_count: audit.stale.length,
      orphaned_count: audit.orphaned.length,
      draft_count: audit.draft.length,
      zombie_count: zombie.length,
      unassigned_count: unassigned.length,
      duplicate_count: duplicates.length,
    })
    await flushAndExit(blocking > 0 ? 1 : 0)
  }

  // ─── list ────────────────────────────────────────────────────────────
  if (subCommand === 'list') {
    const query: string[] = []
    if (flags['stale']) query.push('stale=true')
    if (flags['docs']) query.push('docs=true')
    if (flags['auto']) query.push('auto=true')
    if (flags['manual']) query.push('manual=true')
    if (flags['status']) query.push(`status=${encodeURIComponent(flags['status'] as string)}`)
    // --tag is repeatable; OR semantics (a topic matching ANY tag is included).
    const listTags = collectMulti(args, ['--tag'])
    if (listTags.length > 0) query.push(`tags=${encodeURIComponent(listTags.join(','))}`)
    if (flags['repo']) {
      query.push(`repo=${encodeURIComponent(flags['repo'] as string)}`)
    } else if (flags['auto']) {
      // Scope --auto to the current repo to avoid showing orphaned topics from deleted repos
      const currentRemote = getGitRemote()
      if (currentRemote) {
        try {
          const repos = (await api.get(`/workspaces/${workspaceSlug}/repos`)) as Array<{ id: string; github_org: string; github_repo: string }>
          const match = repos.find((r) => r.github_org === currentRemote.org && r.github_repo === currentRemote.repo)
          if (match) query.push(`repo=${encodeURIComponent(match.id)}`)
        } catch { /* silently skip */ }
      }
    }
    // F10.2 — bound the read SERVER-SIDE where we safely can. The API's
    // listSummaries already orders drifted-first then newest (identical to the
    // client-side sort below), so forwarding `limit` yields the same top-N without
    // shipping the whole workspace. Two cases keep the unbounded fetch:
    //   • --suggested: the API has no draft/proposed filter, so we must fetch all
    //     and filter client-side (a server limit would cut the set before filtering).
    //     We still cap the prefetch to protect large workspaces.
    //   • --all (with no --limit): the caller explicitly asked for everything.
    const limitFlag = flags['limit'] ? parseInt(flags['limit'] as string, 10) : NaN
    const explicitLimit = Number.isFinite(limitFlag) && limitFlag > 0 ? limitFlag : null
    // --cursor pages a large vault by offset (the GET /topics list accepts ?offset;
    // privacy + ranking + limit all run in SQL BEFORE the offset, so a page never
    // leaks across the visibility boundary). Opaque base64url {o:offset}.
    const listOffset = decodeListCursor(flags['cursor'])
    const canBoundServerSide = !flags['suggested'] && !(flags['all'] && !explicitLimit)
    let serverLimit: number | null = null
    if (canBoundServerSide) {
      // Default human cap is 40; honor an explicit --limit. Server applies the
      // same ranking, so the bounded page equals the head of the full list.
      serverLimit = explicitLimit ?? (isJSON ? null : 40)
      if (serverLimit != null) query.push(`limit=${serverLimit}`)
      if (listOffset > 0) query.push(`offset=${listOffset}`)
    } else if (flags['suggested']) {
      // Prefetch cap for --suggested: fetch enough to satisfy the request after
      // client-side filtering without flooding large workspaces. Use 4× the
      // requested limit (min 200) so the filter has headroom.
      const prefetchCap = explicitLimit ? Math.max(200, explicitLimit * 4) : 200
      query.push(`limit=${prefetchCap}`)
    }
    const qs = query.length > 0 ? `?${query.join('&')}` : ''
    try {
      let summaries = (await api.get(`/workspaces/${workspaceSlug}/topics${qs}`)) as CanonicalSummary[]
      // "Suggested" = the not-yet-Knowledge pile a human still has to look at:
      // Notes (private drafts) + notes up for review. The list API doesn't
      // filter by it, so we do it client-side off the canonical status.
      if (flags['suggested']) {
        summaries = summaries.filter(
          (s) => s.classification?.status === 'draft' || s.classification?.status === 'proposed',
        )
      }
      // Bound the output so a large workspace can't flood the caller's context.
      // Ranking, not stuffing: drifted (actionable) first, then most-recently
      // updated. Browse-all is opt-in via --all / --limit N.
      const total = summaries.length
      // Defensive re-sort: harmless when the server already returned drifted-first
      // (the common bounded path), load-bearing for the --suggested/--all branches
      // that still fetch unbounded and filter/order client-side.
      summaries.sort((a, b) => {
        const drift = (b.stale?.is_stale ? 1 : 0) - (a.stale?.is_stale ? 1 : 0)
        if (drift !== 0) return drift
        return (b.metadata?.last_updated || '').localeCompare(a.metadata?.last_updated || '')
      })
      // --json is the machine interface: full set by default, only an explicit
      // --limit/--all bounds it. Human output defaults to 40 to protect the
      // reader's context; --all / --limit override. (When the read was bounded
      // server-side the slice is a no-op — the page already fits the cap.)
      const cap = (flags['all'] && !explicitLimit) ? total : (explicitLimit ?? (isJSON ? total : 40))
      const shown = summaries.slice(0, cap)
      if (total === 0 && !isJSON) {
        if (flags['suggested']) {
          console.log('No suggested topics. Create topics explicitly with `driftless context add`.')
        } else {
          console.log('No context topics yet.')
          console.log('\nNext:')
          console.log('  driftless context add "auth-boundaries" --what "..." --how "..." --pattern "src/auth/**"')
        }
      }
      // A full server-bounded page means there may be more — mint a next cursor.
      const pageFull = serverLimit != null && shown.length === serverLimit
      const nextCursor = pageFull ? encodeListCursor(listOffset + serverLimit!) : null
      if (isJSON) {
        emitJSON(nextCursor ? { topics: shown, nextCursor, has_more: true } : shown)
      } else {
        renderSummaryHuman(shown)
        if (nextCursor) {
          console.log(`\nNext page: driftless context list --cursor ${nextCursor}${explicitLimit ? ` --limit ${explicitLimit}` : ''} (narrow with --stale / --tag / --area, or --all)`)
        } else if (total > shown.length) {
          console.log(`\nShowing ${shown.length} of ${total} — narrow with --stale / --suggested / --repo, or see all with --all / --limit N`)
        }
      }
      analyticsEvent('cli_context_list', workspaceSlug, {
        filter: flags['suggested'] ? 'suggested' : 'all',
      })
      await flushAnalytics()
    } catch (e: any) {
      console.error(`List failed: ${formatError(e)}`)
      await analyticsCommandError('context list', workspaceSlug, formatError(e))
      await flushAnalytics()
      process.exit(1)
    }
    return
  }

  // ─── get ─────────────────────────────────────────────────────────────
  // ─── retrieve ──────────────────────────────────────────────────────────
  // The unified intent ranker (POST /topics/retrieve): one bounded command for
  // "find the right context for this task" across a large vault — query and/or
  // files, structured filters, ranked drifted-first with trust + why_matched.
  // The CLI's explicit large-scale retrieval entry point (mirrors MCP
  // driftless_context_retrieve). Per-surface analogues: `collection retrieve`,
  // `broker operations --query`, `project card next`.
  if (subCommand === 'retrieve') {
    const task = positional.slice(1).join(' ').trim()
    const filesFlag = flags['files'] as string | undefined
    const rFiles = filesFlag ? filesFlag.split(',').map((f) => f.trim()).filter(Boolean) : []
    if (!task && rFiles.length === 0) {
      console.error('Usage: driftless context retrieve "<task>" [--files a,b] [--tag t] [--area id] [--stale] [--status s] [--limit n] [--view brief|full] [--explain] [--include-notes]')
      process.exit(1)
    }
    const body: Record<string, unknown> = {}
    if (task) body['query'] = task
    if (rFiles.length) body['files'] = rFiles
    const rTags = collectMulti(args, ['--tag']); if (rTags.length) body['tags'] = rTags
    if (typeof flags['area'] === 'string') body['area'] = flags['area']
    if (flags['stale']) body['stale'] = true
    if (typeof flags['status'] === 'string') body['status'] = flags['status']
    if (flags['include-notes']) body['include_notes'] = true
    const rLimit = flags['limit'] ? parseInt(flags['limit'] as string, 10) : NaN
    if (Number.isFinite(rLimit) && rLimit > 0) body['limit'] = rLimit
    body['view'] = flags['view'] === 'full' ? 'full' : 'brief'
    try {
      const res = (await api.post(`/workspaces/${workspaceSlug}/topics/retrieve`, body)) as any
      if (isJSON) { emitJSON(res); await flushAndExit(0) }
      const results = Array.isArray(res?.results) ? res.results : []
      if (!results.length) {
        console.log(`No recorded context for "${task || rFiles.join(', ')}". Read the code, then leave a note: driftless context add <slug> --what "..." --pattern "..."`)
        await flushAndExit(0)
      }
      for (const r of results) {
        const c = r.context ?? {}
        const slug = c.slug ?? c.topic ?? c.name ?? '?'
        const conf = typeof r.confidence === 'number' ? ` conf ${r.confidence}` : ''
        console.log(`[${r.trust ?? '—'}] ${slug}  (${r.match_type ?? 'match'}${conf})${r.stale ? '  ⚠ drifted' : ''}`)
        const blurb = c.summary ?? c.what
        if (blurb) console.log(`    ${blurb}`)
        if (flags['explain'] && Array.isArray(r.why_matched) && r.why_matched.length) console.log(`    why: ${r.why_matched.join('; ')}`)
        if (r.next_action) console.log(`    → ${r.next_action}`)
      }
      if (res?.shown != null && res?.total_candidates != null && res.total_candidates > res.shown) {
        console.log(`\nShowing ${res.shown} of ${res.total_candidates} — raise --limit or narrow the query/filters.`)
      }
      analyticsEvent('cli_context_retrieve', workspaceSlug, { result_count: results.length })
      await flushAnalytics()
    } catch (e: any) {
      console.error(`Retrieve failed: ${formatError(e)}`)
      await flushAnalytics()
      await flushAndExit(1)
    }
    await flushAndExit(0)
  }

  if (subCommand === 'get') {
    const slug = positional[1]
    const filesFlag = flags['files'] as string | undefined
    const diffFlag = !!flags['diff']

    // File/diff-grounded retrieval
    if (filesFlag || diffFlag) {
      let files: string[]
      if (filesFlag) {
        files = filesFlag.split(',').map((f) => f.trim()).filter(Boolean)
        if (files.length === 0) {
          console.error('No files provided. Usage: driftless context get --files "path1,path2"')
          process.exit(1)
        }
      } else {
        files = getChangedFilesList()
      }
      if (files.length === 0) {
        if (!isJSON) {
          console.log('No local changes detected.')
        } else {
          emitJSON([])
        }
        process.exit(0)
      }
      // Warn about missing files
      const missingFiles = files.filter((f) => !existsSync(resolve(process.cwd(), f)))
      if (missingFiles.length > 0 && !isJSON) {
        console.error(`File(s) not found locally: ${missingFiles.join(', ')}. Matching by pattern only.`)
      }
      try {
        const results = (await api.post(
          `/workspaces/${workspaceSlug}/topics/match-files`,
          { files },
        )) as MatchFilesResult[]
        if (results.length === 0 && !isJSON) {
          console.log(`No context topics match these files.`)
        }
        if (isJSON) {
          emitJSON(results)
        } else {
          renderMatchFilesHuman(results, files)
        }
        // Second drift source (local git, no GitHub App). Opt-in via --mark:
        // persist drift for the matched topics so a team without the App still
        // gets a re-review signal. Default stays display-only — WIP never flips
        // team state unless the dev explicitly asks.
        if (flags['mark'] && diffFlag) {
          try {
            const res = (await api.post(
              `/workspaces/${workspaceSlug}/topics/mark-drift`,
              { files, source: 'local git' },
            )) as { marked: number; topics: string[] }
            if (!isJSON) {
              if (res.marked > 0) {
                console.log(`\n↻ marked ${res.marked} topic${res.marked === 1 ? '' : 's'} drifted: ${res.topics.join(', ')}`)
                console.log(`  re-review them in the dashboard's Review Queue, or update them here.`)
              } else {
                console.log(`\n↻ --mark: no covered topics to flag.`)
              }
            }
          } catch (e: any) {
            if (!isJSON) console.error(`Could not mark drift: ${formatError(e)}`)
          }
        }
        analyticsEvent('cli_context_get_files', workspaceSlug, {
          file_count: files.length,
          matched: results.length > 0,
          marked: !!flags['mark'],
        })
        await flushAnalytics()
      } catch (e: any) {
        console.error(`Match failed: ${formatError(e)}`)
        await analyticsCommandError('context get', workspaceSlug, formatError(e))
        await flushAnalytics()
        process.exit(1)
      }
      return
    }

    // Canonical topic retrieval
    if (!slug) {
      console.error('Usage: driftless context get <topic>')
      console.error('       driftless context get --files "path1,path2"')
      console.error('       driftless context get --diff [--mark]   (--mark persists drift for matched topics)')
      process.exit(1)
    }
    let ctx: CanonicalContext
    try {
      ctx = (await api.get(`/workspaces/${workspaceSlug}/topics/${slug}`)) as CanonicalContext
    } catch {
      // Only a failed fetch means the topic isn't there. A render error
      // below is a CLI bug, not a missing topic — don't mask it as
      // "not found" (that was the spurious message after a successful get).
      console.error(`Topic '${slug}' not found.`)
      await analyticsCommandError('context get', workspaceSlug, `Topic '${slug}' not found.`)
      await flushAnalytics()
      process.exit(1)
    }
    analyticsEvent('cli_context_get', workspaceSlug, { topic: slug })
    if (isJSON) {
      // --json is the agent-facing interface — emit the full payload. Never
      // truncate here: the entire value IS the content field, so a 500-char cap
      // means the agent literally cannot read the topic.
      emitJSON(ctx)
    } else {
      renderContextHuman(ctx)
    }
    await flushAnalytics()
    return
  }

  // ─── search ──────────────────────────────────────────────────────────
  if (subCommand === 'search') {
    // ARP v2 — Directory view: `--type external` browses topics that carry an
    // external anchor (no keyword needed), via the list endpoint's has_external.
    if (flags['type'] === 'external') {
      try {
        const results = (await api.get(externalDirectoryPath(workspaceSlug))) as CanonicalSummary[]
        if (isJSON) emitJSON(results)
        else if (results.length === 0) console.log('No topics with external anchors yet.')
        else renderSummaryHuman(results)
        analyticsEvent('cli_context_search', workspaceSlug, { result_count: results.length, directory: true })
        await flushAndExit(0)
      } catch (e: any) {
        console.error(`Directory failed: ${formatError(e)}`)
        await flushAndExit(1)
      }
      return
    }
    const query = positional.slice(1).join(' ')
    if (!query) {
      console.error('Usage: driftless context search "<query>" [--tag <name>] [--limit <n>] [--include-notes]')
      console.error('       (Notes — private drafts — are excluded by default; --include-notes adds your own.)')
      process.exit(1)
    }
    try {
      // Gov v2 — Notes (private drafts) are excluded from search by default;
      // --include-notes also searches your own notes.
      const includeNotes = flags['include-notes'] ? '&include_notes=true' : ''
      // --tag scopes the search to a category ("search within billing").
      // Repeatable; OR semantics across tags.
      const searchTags = collectMulti(args, ['--tag'])
      const tagsParam = searchTags.length > 0 ? `&tags=${encodeURIComponent(searchTags.join(','))}` : ''
      // --limit bounds the result set server-side (the search endpoint accepts
      // ?limit and clamps it to its hard cap of 50; no flag → that 50 default,
      // back-compat). The server-side cap means there's no "--all" beyond 50.
      const searchLimitFlag = flags['limit'] ? parseInt(flags['limit'] as string, 10) : NaN
      const searchLimit = Number.isFinite(searchLimitFlag) && searchLimitFlag > 0 ? searchLimitFlag : null
      const limitParam = searchLimit !== null ? `&limit=${searchLimit}` : ''
      const results = (await api.get(
        `/workspaces/${workspaceSlug}/topics/search?q=${encodeURIComponent(query)}${includeNotes}${tagsParam}${limitParam}`,
      )) as CanonicalSummary[]
      if (results.length === 0 && !isJSON) {
        console.log(`No context topics matching "${query}".`)
      }
      if (isJSON) {
        emitJSON(results)
      } else {
        renderSummaryHuman(results)
      }
      analyticsEvent('cli_context_search', workspaceSlug, { result_count: results.length })
      await flushAnalytics()
    } catch (e: any) {
      console.error(`Search failed: ${formatError(e)}`)
      await analyticsCommandError('context search', workspaceSlug, formatError(e))
      await flushAnalytics()
      process.exit(1)
    }
    return
  }

  // ─── add ─────────────────────────────────────────────────────────────
  if (subCommand === 'add') {
    if (flags['help'] === true || positional[1] === 'help') {
      printAddHelp()
      process.exit(0)
    }
    const name = positional[1]
    if (!name) {
      printAddHelp(process.stderr)
      process.exit(1)
    }
    const slug = toSlug(name)
    let fileContent: string | undefined
    if (flags['file']) {
      try {
        fileContent = readFileSync(flags['file'] as string, 'utf-8')
      } catch {
        console.error(`File not found: ${flags['file']}`)
        process.exit(1)
      }
    }
    let content: string | undefined
    if (flags['content']) {
      const raw = flags['content'] as string
      if (raw.startsWith('@')) {
        try {
          content = readFileSync(raw.slice(1), 'utf-8')
        } catch {
          console.error(`File not found: ${raw.slice(1)}`)
          process.exit(1)
        }
      } else {
        content = raw
      }
    }
    // --file anchors the doc; the file body becomes the topic content
    // so it is readable as markdown (content field), not just stored
    // as a raw file blob (file_content).
    if (flags['file'] && !content) {
      content = fileContent
    }
    const tags: string[] | undefined = flags['tags']
      ? (flags['tags'] as string).split(',').map((t: string) => t.trim()).filter(Boolean)
      : undefined
    const rels = collectMulti(args, ['--rel']).map(checkRel)
    // Invariants / required checks on CREATE (repeatable). The API DTO accepts
    // these as arrays; without sending them, `add --invariant` was silently
    // dropped at the whitelist pipe.
    const addInvariants = collectMulti(args, ['--invariant'])
    const addChecks = collectMulti(args, ['--check'])
    // Singular append forms on CREATE. `update` reads `--gotcha`/`--decision`
    // (append) but `add` only read the plural replace forms, so `add --gotcha`
    // was silently dropped (the topic was created without it). On a fresh topic
    // append == replace, so merge the plural value with the singular ones.
    const addGotchas = collectMulti(args, ['--gotcha'])
    const addDecisions = collectMulti(args, ['--decision'])
    // Validate free-text field sizes before hitting the API.
    checkFieldSize('--what', flags['what'] as string | undefined)
    checkFieldSize('--how', flags['how'] as string | undefined)
    checkFieldSize('--decisions', flags['decisions'] as string | undefined)
    checkFieldSize('--gotchas', flags['gotchas'] as string | undefined)
    checkFieldSize('--ownership', flags['ownership'] as string | undefined)
    checkFieldSize('--content', content, MAX_CONTENT_SIZE)
    // --pattern is repeatable. parseArgs only keeps the last; collectMulti
    // captures every occurrence so `--pattern a --pattern b` sends [a, b].
    // ARP v2 — typed anchors + rules. file_pattern anchors fold into patterns[]
    // (drift source of truth); external anchors go to anchors[].
    let externalAnchors: Array<Record<string, string>> = []
    let anchorFilePatterns: string[] = []
    let rules: Array<Record<string, unknown>> = []
    try {
      const parsedAnchors = collectMulti(args, ['--anchor']).map(parseAnchorFlag)
      const split = splitAnchors(parsedAnchors)
      externalAnchors = split.external
      anchorFilePatterns = split.filePatterns
      rules = collectMulti(args, ['--rule']).map(parseRuleFlag)
    } catch (e: any) {
      console.error(`Error: ${e?.message ?? e}`)
      process.exit(1)
    }
    const patterns = [...collectMulti(args, ['--pattern']), ...anchorFilePatterns]
    for (const p of patterns) {
      if (hasCommaOutsideBraces(p)) {
        console.error(`Error: pattern "${p}" contains a comma outside braces. Use repeated --pattern instead.`)
        console.error('  Example: --pattern "src/auth/**" --pattern "src/users/**"')
        process.exit(1)
      }
    }
    // Validate patterns against the local checkout BEFORE we POST. Zero
    // matches block the create; over-broad and suspicious-noise warn.
    // Stats go to analytics so we can measure how often the validator
    // catches a bad anchor on the first try.
    if (patterns.length > 0) {
      const validation = validateAndReportPatterns(patterns)
      analyticsEvent('cli_pattern_validation', workspaceSlug, {
        command: 'context add',
        ...validation,
      })
      if (!validation.ok) {
        console.error('')
        console.error('  Topic not created. Verify the glob(s) against your repo root, or use --where for explicit file paths.')
        await flushAndExit(1)
      }
    }
    if (flags['dry-run']) {
      if (isJSON) {
        emitJSON({
          dry_run: true,
          action: 'add',
          topic: name,
          fields: {
            what: flags['what'], how: flags['how'], patterns,
            where: flags['where'], decisions: flags['decisions'], ownership: flags['ownership'],
          },
        })
      } else {
        console.log(`Would create topic '${name}':`)
        for (const p of patterns) console.log(`  pattern: ${p}`)
        if (flags['where']) console.log(`  where:   ${flags['where']}`)
        if (flags['what']) console.log(`  what:    ${flags['what']}`)
        if (flags['how']) console.log(`  how:     ${flags['how']}`)
        console.log('\n(Dry run — no changes written to Driftless Cloud)')
      }
      process.exit(0)
    }
    warnIfDocShapedField(flags)
    // Secret hygiene: build the exact request body, then scan THAT locally so a
    // pasted credential never leaves the machine and the scanned value can't
    // diverge from what's sent. --allow-secrets overrides (and is forwarded so
    // the server gate agrees). The server runs the same scan for non-CLI clients.
    const allowSecrets = isAllowSecrets(flags)
    // --area <name|id>: resolve to an area_id, auto-creating the area when the
    // name is new (prints "created area '<name>'"). File the topic there on POST.
    let addAreaId: string | null = null
    if (flags['area']) {
      try {
        addAreaId = await resolveAreaId(workspaceSlug, flags['area'], isJSON)
      } catch (e: any) {
        console.error(`Failed to resolve --area: ${formatError(e)}`)
        await flushAndExit(1)
      }
    }
    const addBody = {
      name,
      title: flags['title'] as string | undefined,
      what: flags['what'] as string | undefined,
      how: flags['how'] as string | undefined,
      where_files: flags['where'] ? [flags['where'] as string] : undefined,
      patterns: patterns.length > 0 ? patterns : undefined,
      decisions: mergeAppendField(flags['decisions'], addDecisions),
      gotchas: mergeAppendField(flags['gotchas'], addGotchas),
      ownership: flags['ownership'] as string | undefined,
      file_content: fileContent,
      content,
      tags,
      status: flags['status'] as string | undefined,
      invariants: addInvariants.length > 0 ? addInvariants : undefined,
      required_checks: addChecks.length > 0 ? addChecks : undefined,
      area_id: addAreaId ?? undefined,
      anchors: externalAnchors.length > 0 ? externalAnchors : undefined,
      rules: rules.length > 0 ? rules : undefined,
      allow_secrets: allowSecrets || undefined,
    }
    checkSecretsOrExit(addBody, allowSecrets)
    try {
      const result = (await api.post(`/workspaces/${workspaceSlug}/topics`, addBody)) as CanonicalContext
      // Rich props so the funnel can correlate creates with surrounding
      // signals (recent uncovered PRs in the same workspace, validator
      // outcomes, etc). Event name kept legacy for PostHog continuity.
      analyticsEvent('context_created', workspaceSlug, {
        topic: name,
        pattern_count: patterns.length,
        has_what: !!flags['what'],
        has_how: !!flags['how'],
        has_content: !!content,
        has_tags: !!(tags && tags.length > 0),
        rel_count: rels.length,
        status: (flags['status'] as string | undefined) ?? 'draft',
      })
      if (isJSON) {
        emitJSON(result)
      } else {
        console.log(`Topic '${name}' created.`)
        for (const p of patterns) console.log(`  pattern: ${p}`)
        if (flags['what']) console.log(`  what: ${flags['what']}`)
        if (flags['how']) console.log(`  how: ${flags['how']}`)
      }
      // Anchoring discipline: a fresh topic that matches > 100 files is
      // almost always over-broad (think `src/**` on a 2k-file repo). Warn
      // non-blockingly at the friction point — see [[structural-feedback-loops]].
      warnIfOverBroadAnchor(patterns)
      if (!isJSON) printVaultValidation(result)
      if (rels.length > 0) {
        // POST /topics returns the raw Topic entity (has `.slug`), not
        // the normalized CanonicalContext shape (has `.topic`). Tolerate
        // both, then fall back to the user-provided name — the controller
        // derives the slug as `name.toLowerCase().replace(/\s+/g, '-')`,
        // and the CLI already enforces a valid slug grammar on input.
        const slug = result.topic ?? (result as unknown as { slug?: string }).slug ?? name
        for (const rel of rels) {
          try {
            await api.post(`/workspaces/${workspaceSlug}/topics/${slug}/relations`, {
              to_slug: rel.to_slug,
              relation_type: rel.relation_type,
            })
            if (!isJSON) console.log(`  ↳ ${rel.relation_type} → ${rel.to_slug}`)
          } catch (relErr: any) {
            if (!isJSON) console.error(`  ✗ relation failed: ${rel.relation_type}:${rel.to_slug} — ${formatError(relErr)}`)
          }
        }
      }
      await flushAnalytics()
    } catch (e: any) {
      console.error(`Failed to create topic: ${formatError(e)}`)
      await analyticsCommandError('context add', workspaceSlug, formatError(e))
      await flushAnalytics()
      process.exit(1)
    }
    return
  }

  // ─── update ──────────────────────────────────────────────────────────
  if (subCommand === 'update') {
    if (flags['help'] === true || positional[1] === 'help') {
      printUpdateHelp()
      process.exit(0)
    }
    const slug = positional[1]
    if (!slug) {
      printUpdateHelp(process.stderr)
      process.exit(1)
    }
    const updates: Record<string, unknown> = {}
    if (flags['title']) updates.title = flags['title']
    if (flags['what']) updates.what = flags['what']
    if (flags['how']) updates.how = flags['how']
    if (flags['where']) updates.where_files = [flags['where']]
    if (flags['decisions']) updates.decisions = flags['decisions']
    if (flags['gotchas']) updates.gotchas = flags['gotchas']
    if (flags['ownership']) updates.ownership = flags['ownership']
    if (flags['status']) updates.status = flags['status']
    if (flags['content']) {
      const raw = flags['content'] as string
      if (raw.startsWith('@')) {
        try {
          updates.content = readFileSync(raw.slice(1), 'utf-8')
        } catch {
          console.error(`File not found: ${raw.slice(1)}`)
          process.exit(1)
        }
      } else {
        updates.content = raw
      }
    }
    if (flags['tags']) {
      updates.tags = (flags['tags'] as string).split(',').map((t: string) => t.trim()).filter(Boolean)
    }
    // ARP — edit external anchors / structured rules (full-replace). This is how
    // an existing topic becomes a Route (add a connection anchor) or how a Route's
    // criterion is edited. file_pattern anchors fold into patterns[] as on create.
    const updParsedAnchors = collectMulti(args, ['--anchor']).map(parseAnchorFlag)
    if (updParsedAnchors.length > 0) {
      const split = splitAnchors(updParsedAnchors)
      updates.anchors = split.external
      if (split.filePatterns.length > 0) {
        updates.add_patterns = [...((updates.add_patterns as string[]) ?? []), ...split.filePatterns]
      }
    }
    const updRules = collectMulti(args, ['--rule']).map(parseRuleFlag)
    if (updRules.length > 0) updates.rules = updRules
    // --area <name|id>: migrate this knowledge to an area. Resolve to an area_id
    // (auto-creating the area when the name is new) and PATCH area_id on the topic.
    // Under --dry-run we don't resolve (so a preview never auto-creates an area) —
    // we just record the requested value so the preview shows it.
    if (flags['area']) {
      if (flags['dry-run']) {
        updates.area = flags['area']
      } else {
        try {
          updates.area_id = await resolveAreaId(workspaceSlug, flags['area'], isJSON)
        } catch (e: any) {
          console.error(`Failed to resolve --area: ${formatError(e)}`)
          await flushAndExit(1)
        }
      }
    }
    const rels = collectMulti(args, ['--rel']).map(checkRel)

    // Warn when append + replace forms are mixed so users don't silently
    // clobber team history while thinking they only appended.
    warnIfConflictingAppend(args, flags, 'decision', 'decisions')
    warnIfConflictingAppend(args, flags, 'gotcha', 'gotchas')
    warnIfConflictingAppend(args, flags, 'invariant', 'invariants')
    warnIfConflictingAppend(args, flags, 'check', 'checks')

    // Validate free-text field sizes before hitting the API.
    checkFieldSize('--what', flags['what'] as string | undefined)
    checkFieldSize('--how', flags['how'] as string | undefined)
    checkFieldSize('--decisions', flags['decisions'] as string | undefined)
    checkFieldSize('--gotchas', flags['gotchas'] as string | undefined)
    checkFieldSize('--ownership', flags['ownership'] as string | undefined)
    checkFieldSize('--content', updates.content as string | undefined, MAX_CONTENT_SIZE)

    // Repeatable anchor flags — each one a separate semantics:
    //   --pattern (repeatable)        REPLACE entire patterns[]
    //   --add-pattern (repeatable)    APPEND (idempotent dedupe)
    //   --remove-pattern (repeatable) REMOVE (idempotent)
    // All three batched into a single PATCH so the API does one atomic
    // SQL UPDATE and the version bump fires once.
    const replacePatterns = collectMulti(args, ['--pattern'])
    const addPatterns = collectMulti(args, ['--add-pattern'])
    const removePatterns = collectMulti(args, ['--remove-pattern'])
    // --pattern REPLACES the whole anchor set; --add/--remove-pattern MUTATE it.
    // Combining them is ambiguous and the server resolves it as replace-wins, so
    // the mutations would be silently dropped (while the summary still reports
    // them). Refuse the combo instead — pick one mode.
    if (replacePatterns.length > 0 && (addPatterns.length > 0 || removePatterns.length > 0)) {
      console.error('Error: --pattern (replace the whole anchor set) cannot be combined with --add-pattern/--remove-pattern (mutate it).')
      console.error('  Use --pattern to set the anchors outright, OR --add-pattern/--remove-pattern to adjust them — not both.')
      await flushAndExit(1)
    }
    // Validate newly introduced patterns against the local checkout BEFORE
    // we PATCH. Zero matches block; over-broad and suspicious-noise warn.
    // --remove-pattern is exempt: removing an anchor never needs validation.
    const newPatterns = [...replacePatterns, ...addPatterns]
    if (newPatterns.length > 0) {
      const validation = validateAndReportPatterns(newPatterns)
      analyticsEvent('cli_pattern_validation', workspaceSlug, {
        command: 'context update',
        ...validation,
      })
      if (!validation.ok) {
        console.error('')
        console.error('  Update aborted. Verify the glob(s) against your repo root or use --where for explicit file paths.')
        await flushAndExit(1)
      }
    }
    if (replacePatterns.length > 0) updates.patterns = replacePatterns
    if (addPatterns.length > 0) updates._add_patterns = addPatterns
    if (removePatterns.length > 0) updates._remove_patterns = removePatterns

    // Collect multi-value append flags (supports --gotcha passed multiple times)
    const gotchaValues = collectMulti(args, ['--gotcha', '--gotchas_append'])
    const decisionValues = collectMulti(args, ['--decision'])
    const invariantValues = collectMulti(args, ['--invariant'])
    const checkValues = collectMulti(args, ['--check'])
    if (gotchaValues.length > 0) updates._append_gotchas = gotchaValues.join('\n')
    if (decisionValues.length > 0) updates._append_decisions = decisionValues.join('\n')
    if (invariantValues.length > 0) updates._append_invariants = invariantValues.join('\n')
    if (checkValues.length > 0) updates._append_required_checks = checkValues.join('\n')
    // Replace forms (repeatable → whole-array replace, mirroring --pattern).
    // invariants/required_checks are text[] columns; --invariants/--checks
    // replace the entire array. Replace wins over append at the API (the scalar
    // set overrides the array_append expression, same as --gotchas vs --gotcha).
    const invariantsReplace = collectMulti(args, ['--invariants'])
    const checksReplace = collectMulti(args, ['--checks'])
    if (invariantsReplace.length > 0) updates.invariants = invariantsReplace
    if (checksReplace.length > 0) updates.required_checks = checksReplace
    const enforceFlag = flags['enforce'] as string | boolean | undefined
    if (enforceFlag && typeof enforceFlag === 'string') {
      updates._enforce_rule = enforceFlag
    } else if (enforceFlag === true) {
      updates._enforce_rule = true
    }

    const eventParts: string[] = []
    if (gotchaValues.length > 0) eventParts.push(`gotcha appended`)
    if (invariantValues.length > 0) eventParts.push(`invariant appended`)
    if (checkValues.length > 0) eventParts.push(`check appended`)
    if (addPatterns.length > 0) eventParts.push(`${addPatterns.length} pattern${addPatterns.length === 1 ? '' : 's'} added`)
    if (removePatterns.length > 0) eventParts.push(`${removePatterns.length} pattern${removePatterns.length === 1 ? '' : 's'} removed`)
    if (replacePatterns.length > 0) eventParts.push(`patterns replaced`)
    if (enforceFlag) eventParts.push(`enforce requested`)
    if (eventParts.length > 0) {
      updates._event_detail = `CONTEXT_UPDATED: ${eventParts.join(', ')}`
    }

    if (Object.keys(updates).length === 0 && rels.length === 0) {
      console.error('No fields to update.\n')
      printUpdateHelp(process.stderr)
      process.exit(1)
    }

    // Auto-attach current repo to topic (cross-repo)
    const currentRemote = getGitRemote()
    if (currentRemote) {
      try {
        const repos = (await api.get(`/workspaces/${workspaceSlug}/repos`)) as Array<{ id: string; github_org: string; github_repo: string }>
        const match = repos.find((r) => r.github_org === currentRemote.org && r.github_repo === currentRemote.repo)
        if (match) updates._add_repo = match.id
      } catch {
        // silently skip — cross-repo linkage is best-effort
      }
    }

    // --dry-run
    if (flags['dry-run']) {
      if (!isJSON) {
        console.log(`Would update topic '${slug}':`)
        for (const [key, value] of Object.entries(updates)) {
          if (key.startsWith('_')) continue
          console.log(`  ${key}: ${value}`)
        }
        if (addPatterns.length > 0) console.log(`  + add ${addPatterns.length} pattern${addPatterns.length === 1 ? '' : 's'}: ${addPatterns.join(', ')}`)
        if (removePatterns.length > 0) console.log(`  − remove ${removePatterns.length} pattern${removePatterns.length === 1 ? '' : 's'}: ${removePatterns.join(', ')}`)
        if (updates._add_repo) console.log(`  _add_repo: ${updates._add_repo} (${currentRemote?.org}/${currentRemote?.repo})`)
        if (rels.length > 0) {
          for (const rel of rels) console.log(`  ↳ ${rel.relation_type} → ${rel.to_slug}`)
        }
        console.log('\n(Dry run — no changes written to Driftless Cloud)')
      }
      if (isJSON) {
        emitJSON({ dry_run: true, topic: slug, updates })
      }
      process.exit(0)
    }

    warnIfDocShapedField(flags)
    // Secret hygiene: scan the exact PATCH payload locally before sending, so a
    // pasted credential never leaves the machine. --allow-secrets overrides and
    // is forwarded so the server gate agrees. scanWritePayload picks the
    // canonical text fields + append params from `updates` itself.
    const allowSecrets = isAllowSecrets(flags)
    checkSecretsOrExit(updates, allowSecrets)
    if (allowSecrets) updates.allow_secrets = true
    try {
      const result = (await api.patch(
        `/workspaces/${workspaceSlug}/topics/${slug}`,
        updates,
      )) as CanonicalContext & { enforcement?: { created: boolean; reason?: string; rule?: { id: string; name: string; type: string; severity: string; auto_suggested: boolean }; invariant_stored: string | boolean } }
      // Rich per-update props so PostHog can correlate context updates
      // with PR-bot signals (gap → anchor conversion is the funnel that
      // matters post-pivot — see project_cli_telemetry_funnel).
      analyticsEvent('cli_context_update', workspaceSlug, {
        topic: slug,
        has_content: !!updates.content,
        has_tags: !!(updates.tags && (updates.tags as any[]).length > 0),
        rel_count: rels.length,
        gotcha_count: gotchaValues.length,
        decision_count: decisionValues.length,
        invariant_count: invariantValues.length,
        check_count: checkValues.length,
        add_pattern_count: addPatterns.length,
        remove_pattern_count: removePatterns.length,
        pattern_replace: replacePatterns.length > 0,
      })
      if (isJSON) {
        emitJSON(result)
      } else {
        // "What changed" header — the full topic re-render below buries the
        // delta in noise (compounded for multi-flag updates). Show appends
        // and scalar sets at a glance before the full context.
        const changes: string[] = []
        if (gotchaValues.length) changes.push(`appended ${gotchaValues.length} gotcha${gotchaValues.length === 1 ? '' : 's'}`)
        if (decisionValues.length) changes.push(`appended ${decisionValues.length} decision${decisionValues.length === 1 ? '' : 's'}`)
        if (invariantValues.length) changes.push(`appended ${invariantValues.length} invariant${invariantValues.length === 1 ? '' : 's'}`)
        if (checkValues.length) changes.push(`appended ${checkValues.length} check${checkValues.length === 1 ? '' : 's'}`)
        if (addPatterns.length) changes.push(`added ${addPatterns.length} pattern${addPatterns.length === 1 ? '' : 's'}`)
        if (removePatterns.length) changes.push(`removed ${removePatterns.length} pattern${removePatterns.length === 1 ? '' : 's'}`)
        if (replacePatterns.length) changes.push(`replaced patterns (${replacePatterns.length})`)
        const scalarKeys = Object.keys(updates).filter((k) => !k.startsWith('_') && k !== 'patterns')
        if (scalarKeys.length) changes.push(`set ${scalarKeys.join(', ')}`)
        if (changes.length) {
          console.log(`Updated topic '${slug}' → v${result.version ?? '?'}: ${changes.join(' · ')}\n`)
        }
        renderContextHuman(result)
        // Anchoring discipline check on the resolved patterns[] post-update.
        warnIfOverBroadAnchor(result.anchors.patterns)
        printVaultValidation(result)
        if (rels.length > 0) {
          for (const rel of rels) {
            try {
              await api.post(`/workspaces/${workspaceSlug}/topics/${slug}/relations`, {
                to_slug: rel.to_slug,
                relation_type: rel.relation_type,
              })
              if (!isJSON) console.log(`  ↳ ${rel.relation_type} → ${rel.to_slug}`)
            } catch (relErr: any) {
              if (!isJSON) console.error(`  ✗ relation failed: ${rel.relation_type}:${rel.to_slug} — ${formatError(relErr)}`)
            }
          }
        }
        if (updates._add_repo && currentRemote) {
          const n = result.anchors.repos.length
          console.log(`\n  ↳ linked ${currentRemote.org}/${currentRemote.repo} — topic now spans ${n} repo${n === 1 ? '' : 's'}`)
        }
        if (result.enforcement) {
          if (result.enforcement.created) {
            console.log(`\n  ✓ enforce: rule created — ${result.enforcement.rule?.name || result.enforcement.rule?.id}`)
            console.log(`    type: ${result.enforcement.rule?.type}  severity: ${result.enforcement.rule?.severity}`)
          } else {
            console.log(`\n  ⚠ enforce: ${result.enforcement.reason}`)
          }
        }
      }
      await flushAnalytics()
    } catch (e: any) {
      console.error(`Failed to update topic: ${formatError(e)}`)
      await analyticsCommandError('context update', workspaceSlug, formatError(e))
      await flushAnalytics()
      process.exit(1)
    }
    return
  }

  // ─── delete ──────────────────────────────────────────────────────────
  if (subCommand === 'delete') {
    const slug = positional[1]
    if (!slug) {
      console.error('Usage: driftless context delete <topic>')
      process.exit(1)
    }

    // --dry-run
    if (flags['dry-run']) {
      if (!isJSON) {
        console.log(`Would delete topic '${slug}'.`)
        console.log('(Dry run — no changes written to Driftless Cloud)')
      }
      if (isJSON) {
        emitJSON({ dry_run: true, action: 'delete', topic: slug })
      }
      process.exit(0)
    }

    try {
      await api.delete(`/workspaces/${workspaceSlug}/topics/${slug}`)
      if (isJSON) {
        emitJSON({ status: 'deleted', topic: slug })
      } else {
        console.log(`Topic '${slug}' deleted.`)
      }
    } catch (e: any) {
      console.error(`Failed to delete topic: ${formatError(e)}`)
      process.exit(1)
    }
    return
  }

  // ─── merge (H2/H3 — fold a duplicate into its survivor, archive the source) ──
  if (subCommand === 'merge') {
    const slug = positional[1]
    const into = flags['into'] as string | undefined
    if (!slug || !into) {
      console.error('Usage: driftless context merge <source-slug> --into <target-slug> [--dry-run]')
      process.exit(1)
    }
    try {
      const result = (await api.post(`/workspaces/${workspaceSlug}/topics/${slug}/merge`, {
        into,
        dry_run: !!flags['dry-run'],
      })) as { dry_run: boolean; anchors_added: number; mentions_added: number; relations_moved: number }
      if (isJSON) {
        emitJSON(result)
      } else if (result.dry_run) {
        console.log(`Would merge '${slug}' into '${into}': +${result.anchors_added} anchors, +${result.mentions_added} mentions, ${result.relations_moved} relations re-pointed; '${slug}' would be archived.`)
        console.log('(Dry run — no changes written to Driftless Cloud)')
      } else {
        console.log(`Merged '${slug}' into '${into}': +${result.anchors_added} anchors, +${result.mentions_added} mentions, ${result.relations_moved} relations re-pointed.`)
        console.log(`'${slug}' archived. Prose was NOT moved — if the source carried unique decisions/gotchas, rewrite them into '${into}' (rule E).`)
      }
    } catch (e: any) {
      console.error(`Failed to merge topic: ${formatError(e)}`)
      process.exit(1)
    }
    return
  }

  // ─── move (re-home a topic to another workspace) ─────────────────────
  if (subCommand === 'move') {
    const slug = positional[1]
    const target = flags['to'] as string | undefined
    if (!slug || !target) {
      console.error('Usage: driftless context move <topic> --to <workspace-slug>')
      process.exit(1)
    }
    if (flags['dry-run']) {
      if (isJSON) emitJSON({ dry_run: true, action: 'move', topic: slug, to: target })
      else console.log(`Would move topic '${slug}' to workspace '${target}' (re-homes as a Note).\n(Dry run — no changes written to Driftless Cloud)`)
      process.exit(0)
    }
    try {
      await api.post(`/workspaces/${workspaceSlug}/topics/${slug}/move`, { target })
      if (isJSON) emitJSON({ status: 'moved', topic: slug, to: target })
      else console.log(`Topic '${slug}' moved to '${target}' — it re-homes there as a Note (re-review in its new workspace).`)
    } catch (e: any) {
      console.error(`Failed to move topic: ${formatError(e)}`)
      process.exit(1)
    }
    return
  }

  // ─── sync ──────────────────────────────────────────────────────────
  if (subCommand === 'sync') {
    const slug = positional[1]
    if (!slug) {
      console.error('Usage: driftless context sync <topic> --doc <path> --files "<glob>"')
      console.error('       driftless context sync <topic> --note "..." --files "<glob>"')
      process.exit(1)
    }
    const docFlag = flags['doc'] as string | undefined
    const noteFlag = flags['note'] as string | undefined
    const filesFlag = flags['files'] as string | undefined
    const patternFlag = flags['pattern'] as string | undefined

    if (!docFlag && !noteFlag) {
      console.error('Provide --doc <path> or --note "..." to anchor.')
      process.exit(1)
    }

    let fileContent: string | undefined
    if (docFlag) {
      try {
        fileContent = readFileSync(docFlag as string, 'utf-8')
      } catch {
        console.error(`File not found: ${docFlag}`)
        process.exit(1)
      }
    }

    const updates: Record<string, unknown> = { last_updated: new Date() }
    if (fileContent) updates.file_content = fileContent
    if (docFlag) {
      updates.anchored_doc_path = docFlag
      updates.status = 'draft'
    }
    if (noteFlag) updates.decisions = noteFlag
    if (filesFlag) updates.where_files = filesFlag.split(',').map((f) => f.trim()).filter(Boolean)
    if (patternFlag) updates.pattern = patternFlag
    updates._event_detail = `SYNC_ADDED: doc=${!!fileContent}, note=${!!noteFlag}, files=${filesFlag || 'none'}`

    // --dry-run
    if (flags['dry-run']) {
      if (!isJSON) {
        console.log(`Would anchor topic '${slug}':`)
        if (docFlag) console.log(`  doc: ${docFlag} (${fileContent ? fileContent.length : 0} chars)`)
        if (noteFlag) console.log(`  note: ${noteFlag}`)
        if (filesFlag) console.log(`  files: ${filesFlag}`)
        if (patternFlag) console.log(`  pattern: ${patternFlag}`)
        console.log('\n(Dry run — no changes written to Driftless Cloud)')
      }
      if (isJSON) {
        emitJSON({ dry_run: true, action: 'sync', topic: slug, doc: docFlag, note: noteFlag, files: filesFlag, pattern: patternFlag })
      }
      process.exit(0)
    }

    // Secret hygiene: a --doc file or --note can carry a credential. Scan the
    // exact payload locally before sending, with --allow-secrets as the override.
    const allowSecrets = isAllowSecrets(flags)
    checkSecretsOrExit(updates, allowSecrets)
    if (allowSecrets) updates.allow_secrets = true

    try {
      const result = (await api.patch(
        `/workspaces/${workspaceSlug}/topics/${slug}`,
        updates,
      )) as CanonicalContext
      if (isJSON) {
        // Agent-facing output — emit the full topic, never truncated. See the
        // note in the `get` --json path above.
        emitJSON(result)
      } else {
        console.log(`Topic '${slug}' synced.`)
        if (docFlag) console.log(`  doc: ${docFlag} (${fileContent ? fileContent.length : 0} chars)`)
        if (noteFlag) console.log(`  note: ${noteFlag}`)
        if (filesFlag) console.log(`  files: ${filesFlag}`)
        if (patternFlag) console.log(`  pattern: ${patternFlag}`)
      }
      analyticsEvent('cli_context_sync', workspaceSlug, { topic: slug })
      await flushAnalytics()
    } catch (e: any) {
      console.error(`Sync failed: ${formatError(e)}`)
      await analyticsCommandError('context sync', workspaceSlug, formatError(e))
      await flushAnalytics()
      process.exit(1)
    }
    return
  }

  // ─── link ────────────────────────────────────────────────────────────
  if (subCommand === 'link') {
    const slug = positional[1]
    if (!slug) {
      console.error('Usage: driftless context link <topic>')
      console.error('  Registers the repo you are in as a place this topic is used (cross-repo).')
      console.error('  Touches only where_repos — what/how/gotchas/decisions are NOT changed.')
      process.exit(1)
    }
    const currentRemote = getGitRemote()
    if (!currentRemote) {
      console.error('Error: not in a git repo with a remote — cannot resolve which repo to link.')
      process.exit(1)
    }
    let repoId: string | undefined
    try {
      const repos = (await api.get(`/workspaces/${workspaceSlug}/repos`)) as Array<{ id: string; github_org: string; github_repo: string }>
      repoId = repos.find((r) => r.github_org === currentRemote.org && r.github_repo === currentRemote.repo)?.id
    } catch (e: any) {
      console.error(`Failed to resolve repos: ${formatError(e)}`)
      process.exit(1)
    }
    if (!repoId) {
      console.error(`Error: ${currentRemote.org}/${currentRemote.repo} is not registered in this workspace.`)
      console.error('  Run `driftless doctor` to diagnose, or create topics without linking.')
      process.exit(1)
    }
    if (flags['dry-run']) {
      if (isJSON) {
        emitJSON({ dry_run: true, action: 'link', topic: slug, repo: `${currentRemote.org}/${currentRemote.repo}`, repo_id: repoId })
      } else {
        console.log(`Would link ${currentRemote.org}/${currentRemote.repo} → topic '${slug}'`)
        console.log('\n(Dry run — no changes written to Driftless Cloud)')
      }
      process.exit(0)
    }
    try {
      const result = (await api.patch(`/workspaces/${workspaceSlug}/topics/${slug}`, {
        _add_repo: repoId,
        _event_detail: `CONTEXT_LINKED: ${currentRemote.org}/${currentRemote.repo}`,
      })) as CanonicalContext
      analyticsEvent('cli_context_link', workspaceSlug, { topic: slug })
      if (isJSON) {
        emitJSON(result)
      } else {
        const n = result.anchors.repos.length
        console.log(`Linked ${currentRemote.org}/${currentRemote.repo} → topic '${slug}' (now spans ${n} repo${n === 1 ? '' : 's'}).`)
      }
      await flushAnalytics()
    } catch (e: any) {
      console.error(`Failed to link topic: ${formatError(e)}`)
      await analyticsCommandError('context link', workspaceSlug, formatError(e))
      await flushAnalytics()
      process.exit(1)
    }
    return
  }


  // ─── relations ────────────────────────────────────────────────────────
  if (subCommand === 'relations') {
    const slug = positional[1]
    if (!slug) {
      console.error('Usage: driftless context relations <slug>')
      process.exit(1)
    }
    try {
      // Two link kinds, fetched together: typed relations (--rel) from the
      // relations endpoint, and [[slug]] mentions from the canonical
      // (references_topics / referenced_by). Shown in separate sections so the
      // strong/weak distinction stays visible.
      const [result, canonical] = (await Promise.all([
        api.get(`/workspaces/${workspaceSlug}/topics/${slug}/relations`),
        api.get(`/workspaces/${workspaceSlug}/topics/${slug}`),
      ])) as [{ outgoing: TopicRelation[]; incoming: TopicRelation[] }, CanonicalContext]
      const mentionsOut = canonical.references_topics ?? []
      const mentionsIn = canonical.referenced_by ?? []
      if (isJSON) { emitJSON({ ...result, mentions: { outgoing: mentionsOut, incoming: mentionsIn } }) } else {
        console.log(`▌ ${slug}\n`)
        if (result.outgoing.length > 0) { console.log('  outgoing (typed)'); for (const r of result.outgoing) console.log(`    → ${r.relation_type} → ${r.to_slug}`) }
        if (result.incoming.length > 0) { console.log('\n  incoming (typed)'); for (const r of result.incoming) console.log(`    ← ${r.relation_type} ← ${r.from_slug}`) }
        if (mentionsOut.length > 0) { console.log('\n  mentions ([[…]])'); for (const m of mentionsOut) console.log(`    → ${m}`) }
        if (mentionsIn.length > 0) { console.log('\n  mentioned by'); for (const m of mentionsIn) console.log(`    ← ${m}`) }
        if (result.outgoing.length === 0 && result.incoming.length === 0 && mentionsOut.length === 0 && mentionsIn.length === 0) console.log('  (no relations or mentions)')
        console.log('')
      }
      analyticsEvent('cli_context_relations', workspaceSlug, { topic: slug, outgoing_count: result.outgoing.length, incoming_count: result.incoming.length, mention_out: mentionsOut.length, mention_in: mentionsIn.length })
      await flushAnalytics()
    } catch (e: any) { console.error(`Failed: ${formatError(e)}`); process.exit(1) }
    return
  }

  // ─── graph ────────────────────────────────────────────────────────────
  if (subCommand === 'graph') {
    const center = positional[1]
    if (!center) { console.error('Usage: driftless context graph <slug> [--depth N]'); process.exit(1) }
    const depth = flags['depth'] ? parseInt(flags['depth'] as string, 10) : 3
    try {
      const qs = `?center=${encodeURIComponent(center)}&depth=${depth}`
      const result = (await api.get(`/workspaces/${workspaceSlug}/topics/graph${qs}`)) as { nodes: Array<{ slug: string; status: string }>; edges: Array<{ from: string; to: string; type: string }> }
      if (isJSON) { emitJSON(result) } else {
        console.log(`▌ ${center}`)
        if (result.nodes.length === 0) console.log('  (no connected topics)')
        for (const node of result.nodes) {
          if (node.slug === center) continue
          const nodeEdges = result.edges.filter((e) => e.from === node.slug || e.to === node.slug)
          for (const e of nodeEdges) {
            if (e.from === node.slug && e.to === center) console.log(`  ← ${e.type} ← ${node.slug} (${node.status})`)
            else if (e.from === center && e.to === node.slug) console.log(`  → ${e.type} → ${node.slug} (${node.status})`)
          }
        }
        console.log('')
      }
      analyticsEvent('cli_context_graph', workspaceSlug, { center, depth, node_count: result.nodes.length, edge_count: result.edges.length })
      await flushAnalytics()
    } catch (e: any) { console.error(`Failed: ${formatError(e)}`); process.exit(1) }
    return
  }

  // ─── share ────────────────────────────────────────────────────────────
  if (subCommand === 'share') {
    const slug = positional[1]
    if (!slug) {
      console.error('Usage: driftless context share <slug> [--with <email|user-id,...> | --revoke]')
      console.error("  (no flags)  Creates a public, read-only link to a topic — send it to anyone, no login needed.")
      console.error('  --with      Share a note with teammates (comma-separated emails or user ids — see `driftless member list`).')
      console.error('  --revoke    Turns the public link off.')
      process.exit(1)
    }
    try {
      // --with: share a note with workspace members (user-to-user grant).
      // Distinct from the public web link — posts to /share-with, not /share.
      if (flags['with']) {
        const tokens = (flags['with'] as string)
          .split(',').map((r) => r.trim()).filter(Boolean)
        if (tokens.length === 0) {
          console.error('Error: --with needs at least one teammate (email or user id, comma-separated).')
          process.exit(1)
        }
        // The /share-with endpoint matches recipients by clerk_user_id. A human
        // only has emails or the id printed by `member list` (a membership uuid),
        // so resolve those to clerk_user_id here — passing the raw uuid otherwise
        // fails with a misleading "not a member". A `user_…` token is already a
        // clerk id and passes through untouched (no member fetch needed).
        let recipients = tokens
        if (tokens.some((t) => !t.startsWith('user_'))) {
          const members = (await api.get(
            `/workspaces/${workspaceSlug}/members`,
          )) as Array<{ id: string; clerk_user_id: string; email?: string }>
          const resolved: string[] = []
          const unresolved: string[] = []
          for (const t of tokens) {
            if (t.startsWith('user_')) { resolved.push(t); continue }
            const lower = t.toLowerCase()
            const m = members.find(
              (mm) => mm.clerk_user_id === t || mm.id === t || (mm.email ?? '').toLowerCase() === lower,
            )
            if (m) resolved.push(m.clerk_user_id)
            else unresolved.push(t)
          }
          if (unresolved.length > 0) {
            console.error(`Error: not a member of this workspace: ${unresolved.join(', ')}`)
            console.error("  Pass each teammate's email or user id — see 'driftless member list'.")
            process.exit(1)
          }
          recipients = resolved
        }
        const result = (await api.post(
          `/workspaces/${workspaceSlug}/topics/${slug}/share-with`,
          { recipients },
        )) as { shared: number; recipients: string[] }
        if (isJSON) { emitJSON(result) }
        else {
          console.log(`▌ ${slug} — shared with ${result.shared} member${result.shared === 1 ? '' : 's'}: ${result.recipients.join(', ')}`)
        }
        analyticsEvent('cli_context_share_with', workspaceSlug, { topic: slug, recipient_count: recipients.length })
        await flushAnalytics()
        return
      }
      if (flags['revoke']) {
        await api.delete(`/workspaces/${workspaceSlug}/topics/${slug}/share`)
        if (isJSON) { emitJSON({ revoked: true, topic: slug }) }
        else { console.log(`Share link for '${slug}' revoked.`) }
      } else {
        const result = (await api.post(
          `/workspaces/${workspaceSlug}/topics/${slug}/share`,
          {},
        )) as { token: string; url: string; already: boolean }
        if (isJSON) { emitJSON(result) } else {
          console.log(`▌ ${slug} — public read-only link${result.already ? ' (already shared)' : ''}:`)
          console.log(`  ${result.url}`)
          console.log(`\n  Anyone with this link can read it — no login. Revoke: driftless context share ${slug} --revoke`)
        }
      }
      analyticsEvent('cli_context_share', workspaceSlug, { topic: slug, revoke: !!flags['revoke'] })
      await flushAnalytics()
    } catch (e: any) {
      console.error(`Share failed: ${formatError(e)}`)
      await analyticsCommandError('context share', workspaceSlug, formatError(e))
      await flushAnalytics()
      process.exit(1)
    }
    return
  }

  // ─── shared (notes shared WITH me) ──────────────────────────────────────
  // `context shared`           list notes teammates shared with you
  // `context shared --read <id>`  mark one shared item read
  // `context shared --all`     mark every shared item read
  if (subCommand === 'shared') {
    type SharedItem = { id: string; topic: string; title: string | null; summary: string; shared_by: string; read_at: string | null }
    try {
      if (typeof flags['read'] === 'string') {
        const id = flags['read']
        const result = (await api.post(`/workspaces/${workspaceSlug}/shared/${encodeURIComponent(id)}/read`, {})) as any
        if (isJSON) { emitJSON(result) }
        else { console.log(`Marked shared note ${id} as read.`) }
      } else if (flags['all']) {
        const result = (await api.post(`/workspaces/${workspaceSlug}/shared/read-all`, {})) as { read?: number }
        if (isJSON) { emitJSON(result) }
        else { console.log(`Marked all shared notes as read${typeof result?.read === 'number' ? ` (${result.read})` : ''}.`) }
      } else {
        const result = (await api.get(`/workspaces/${workspaceSlug}/shared`)) as SharedItem[]
        if (isJSON) { emitJSON(result) }
        else if (!result.length) { console.log('Nothing shared with you yet.') }
        else {
          for (const s of result) {
            const unread = s.read_at == null ? '● ' : '  '
            console.log(`${unread}${s.id}  ${s.title ?? s.topic}  — from ${s.shared_by}`)
            if (s.summary) console.log(`    ${s.summary}`)
          }
          console.log(`\n  Mark read: driftless context shared --read <id>   ·   all: --all`)
        }
      }
      analyticsEvent('cli_context_shared', workspaceSlug, { action: flags['read'] ? 'read' : flags['all'] ? 'read_all' : 'list' })
      await flushAnalytics()
    } catch (e: any) {
      console.error(`Failed: ${formatError(e)}`)
      await analyticsCommandError('context shared', workspaceSlug, formatError(e))
      await flushAnalytics()
      process.exit(1)
    }
    return
  }

  // Trust model is two states: a Note becomes Knowledge once it's merged in.
  // The agent treats Knowledge as the team's source of truth; a Note is a hint.
  // You write notes and propose; merging into Knowledge is an owner/admin act
  // (approve/reject/merge need owner/admin authority) — run it only when an
  // owner/admin explicitly asks, never on your own initiative.
  if (subCommand === 'approve' || subCommand === 'propose' || subCommand === 'reject' || subCommand === 'archive') {
    const slug = positional[1]
    if (!slug) {
      console.error(`Usage: driftless context ${subCommand} <slug>`)
      console.error('  approve  merge a note into the team\'s Knowledge (the agent trusts Knowledge as truth).')
      console.error('  propose  put a note up for review.   reject  send a note back, with feedback.')
      console.error('  archive  retire a topic.')
      process.exit(1)
    }
    const pastTense: Record<string, string> = { approve: 'approved', propose: 'proposed', reject: 'rejected', archive: 'archived' }
    try {
      const result = (await api.post(`/workspaces/${workspaceSlug}/topics/${slug}/${subCommand}`, {})) as any
      if (isJSON) { emitJSON(result) } else {
        const status = result?.classification?.status ?? '?'
        const authoritative = result?.governance?.authoritative
        console.log(`▌ ${slug} — ${pastTense[subCommand]} → status: ${status}${authoritative ? '  ✓ Knowledge' : ''}`)
        if (subCommand === 'approve' && result?.governance?.approved_by) {
          console.log(`  approved by ${result.governance.approved_by}`)
        }
      }
      analyticsEvent(`cli_context_${subCommand}`, workspaceSlug, { topic: slug })
      await flushAnalytics()
    } catch (e: any) {
      console.error(`${subCommand} failed: ${formatError(e)}`)
      await analyticsCommandError(`context ${subCommand}`, workspaceSlug, formatError(e))
      await flushAnalytics()
      process.exit(1)
    }
    return
  }

  // ack / unvouch — Keep / Undo of a Librarian auto-vouch (the autonomous agent
  // auto-promoted a note to Knowledge). Both need owner/admin authority. The ack
  // endpoint is `vouch-ack`; unvouch is `unvouch`.
  if (subCommand === 'ack' || subCommand === 'unvouch') {
    const slug = positional[1]
    if (!slug) {
      console.error(`Usage: driftless context ${subCommand} <slug>`)
      console.error('  ack      keep an agent auto-vouch (confirm the Librarian was right).')
      console.error('  unvouch  undo an agent auto-vouch (send the note back).')
      process.exit(1)
    }
    const path = subCommand === 'ack' ? 'vouch-ack' : 'unvouch'
    try {
      const result = (await api.post(`/workspaces/${workspaceSlug}/topics/${slug}/${path}`, {})) as any
      if (isJSON) { emitJSON(result) } else {
        const status = result?.classification?.status ?? '?'
        console.log(`▌ ${slug} — ${subCommand === 'ack' ? 'kept (auto-vouch acknowledged)' : 'auto-vouch undone'} → status: ${status}`)
      }
      analyticsEvent(`cli_context_${subCommand}`, workspaceSlug, { topic: slug })
      await flushAnalytics()
    } catch (e: any) {
      console.error(`${subCommand} failed: ${formatError(e)}`)
      await analyticsCommandError(`context ${subCommand}`, workspaceSlug, formatError(e))
      await flushAnalytics()
      process.exit(1)
    }
    return
  }

  // Suggested edits: a suggested content change to existing Knowledge; an owner/admin merges it.
  if (subCommand === 'pr') {
    const slug = positional[1]
    if (!slug) {
      console.error('Usage: driftless context pr <slug> [--open --summary "..." <content flags> | --merge <id> | --reject <id>]')
      console.error('  (no flags)      list the suggested edits for the topic.')
      console.error('  --open          open a suggested edit — a content change for an owner/admin to review.')
      console.error('                  content flags: --content "<text>"|@file, --what, --how, --gotchas, --decisions, --tags')
      console.error('  --merge <id>    merge the suggested edit into Knowledge (owner/admin only; run only when asked).')
      console.error('  --reject <id>   close the suggested edit without applying (owner/admin only).')
      process.exit(1)
    }
    try {
      if (typeof flags['merge'] === 'string') {
        const result = (await api.post(`/workspaces/${workspaceSlug}/topics/${slug}/proposals/${flags['merge']}/merge`, {})) as any
        if (isJSON) { emitJSON(result) } else {
          console.log(`▌ suggested edit merged into ${slug} → status: ${result?.classification?.status}${result?.governance?.authoritative ? '  ✓ Knowledge' : ''}`)
        }
        analyticsEvent('cli_context_pr_merge', workspaceSlug, { topic: slug })
      } else if (typeof flags['reject'] === 'string') {
        const result = (await api.post(`/workspaces/${workspaceSlug}/topics/${slug}/proposals/${flags['reject']}/reject`, {})) as any
        if (isJSON) { emitJSON(result) } else { console.log(`▌ suggested edit ${flags['reject']} on ${slug} rejected.`) }
        analyticsEvent('cli_context_pr_reject', workspaceSlug, { topic: slug })
      } else if (flags['open']) {
        const patch: Record<string, unknown> = {}
        for (const f of ['what', 'how', 'gotchas', 'decisions', 'ownership', 'tags']) {
          if (typeof flags[f] === 'string') patch[f] = flags[f]
        }
        if (typeof flags['content'] === 'string') {
          const raw = flags['content'] as string
          patch['content'] = raw.startsWith('@') ? readFileSync(raw.slice(1), 'utf-8') : raw
        }
        if (Object.keys(patch).length === 0) {
          console.error('pr --open needs at least one content change (--content/--what/--how/--gotchas/...).')
          process.exit(1)
        }
        const summary = typeof flags['summary'] === 'string' ? (flags['summary'] as string) : undefined
        const result = (await api.post(`/workspaces/${workspaceSlug}/topics/${slug}/proposals`, { summary, patch })) as any
        if (isJSON) { emitJSON(result) } else {
          console.log(`▌ suggested edit opened on ${slug} (id ${result?.id}) — status: ${result?.status}`)
          console.log(`  Merge it: driftless context pr ${slug} --merge ${result?.id}`)
        }
        analyticsEvent('cli_context_pr_open', workspaceSlug, { topic: slug })
      } else {
        const list = (await api.get(`/workspaces/${workspaceSlug}/topics/${slug}/proposals`)) as any[]
        if (isJSON) { emitJSON(list) } else if (!list || list.length === 0) {
          console.log(`No suggested edits for '${slug}'.`)
        } else {
          console.log(`▌ suggested edits for ${slug}:`)
          for (const p of list) console.log(`  ${String(p.status).padEnd(8)} ${p.id}  ${p.summary ?? ''}  (by ${p.proposed_by ?? 'agent'})`)
        }
      }
      await flushAnalytics()
    } catch (e: any) {
      console.error(`pr failed: ${formatError(e)}`)
      await analyticsCommandError('context pr', workspaceSlug, formatError(e))
      await flushAnalytics()
      process.exit(1)
    }
    return
  }

  // ─── coverage (uncovered source areas) ───────────────────────────────
  if (subCommand === 'coverage') {
    try {
      const result = (await api.get(`/workspaces/${workspaceSlug}/topics/coverage`)) as any
      if (isJSON) { emitJSON(result); await flushAndExit(0); return }
      // The endpoint may report it can't compute coverage (e.g. no repo
      // scanned yet) — handle that gracefully rather than rendering junk.
      if (result && result.available === false) {
        console.log(`Coverage is not available yet${result.reason ? ` — ${result.reason}` : '.'}`)
        await flushAndExit(0); return
      }
      const areas = Array.isArray(result) ? result : (result?.uncovered ?? result?.areas ?? [])
      if (!areas.length) { console.log('No uncovered source areas — coverage looks healthy.'); await flushAndExit(0); return }
      console.log(`Uncovered source areas (${areas.length}):`)
      for (const a of areas) {
        if (typeof a === 'string') { console.log(`  ${a}`); continue }
        const path = a.path ?? a.dir ?? a.area ?? '?'
        const files = a.file_count ?? a.files ?? ''
        console.log(`  ${path}${files ? `  (${files} files)` : ''}`)
      }
      await flushAndExit(0); return
    } catch (e: any) {
      console.error(formatError(e))
      await flushAndExit(1); return
    }
  }

  // ─── events (topic event stream) ─────────────────────────────────────
  if (subCommand === 'events') {
    try {
      const params: string[] = []
      if (flags['limit']) params.push(`limit=${encodeURIComponent(String(flags['limit']))}`)
      if (typeof flags['type'] === 'string') params.push(`type=${encodeURIComponent(flags['type'])}`)
      const q = params.length ? `?${params.join('&')}` : ''
      const result = (await api.get(`/workspaces/${workspaceSlug}/topics/events${q}`)) as any
      const rows = Array.isArray(result) ? result : (result?.events ?? [])
      if (isJSON) { emitJSON(result); await flushAndExit(0); return }
      if (!rows.length) { console.log('No topic events.'); await flushAndExit(0); return }
      for (const e of rows) {
        const type = (e.event_type ?? e.type ?? '?').toString().padEnd(16)
        const topic = e.topic ?? e.slug ?? ''
        console.log(`${e.created_at ?? ''}  ${type} ${topic}`)
      }
      await flushAndExit(0); return
    } catch (e: any) {
      console.error(formatError(e))
      await flushAndExit(1); return
    }
  }

  console.log(`Usage: driftless context <list|get|add|update|approve|propose|reject|archive|ack|unvouch|pr|link|share|shared|delete|search|relations|graph|coverage|events> [args]

Run 'driftless help context' for full reference.`)
}
